import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { canonicalStatusDisplayLabel } from '@boubou/tebox-copy';
import {
  bookingBrandInfoChromeStyles,
  teboxDesignSystem,
  useBookingTebox,
} from '../../styles/tebox-booking';
import {
  bookingBrandInfoPillDataAttrs,
  getBookingBrandInfoPillStyles,
} from '../../utils/booking-brand-info-pill';
import { WalletAddButtons } from '../OnlineBooking/WalletAddButtons';
import {
  giftCardsService,
  type GiftCardDetail,
  type GiftCardSummary,
} from '../../services/gift-cards';
import {
  formatGiftCardEventAmount,
  formatGiftCardBalanceAfter,
  formatPanGroups,
  giftCardEventAmountIsDebit,
  giftCardEventLabel,
  giftCardRoleForCustomer,
  isUsableGiftCard,
} from './booking-gift-card-display';

/** Club brand tokens — aligned with golfer dashboard reservation / wallet cards. */
export type GolferGiftCardBrand = {
  fontFamily: string;
  textColor: string;
  secondaryColor: string;
  primaryColor: string;
  accentColor: string;
  cardBorderRadius: string;
};

const DATE_SHORT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

function giftCardStatusAccentHex(status: string, brand: GolferGiftCardBrand): string {
  const key = status.toLowerCase();
  const pillColors = teboxDesignSystem.components.pill.colors;
  switch (key) {
    case 'active':
      return brand.primaryColor.trim() || pillColors.success;
    case 'expired':
    case 'void':
      return pillColors.error;
    case 'depleted':
      return pillColors.neutral;
    default:
      return pillColors.neutral;
  }
}

function eventDotColor(type: string, brand: GolferGiftCardBrand): string {
  const t = type.toUpperCase();
  if (t === 'PURCHASE' || t === 'REFUND') {
    return brand.primaryColor.trim() || teboxDesignSystem.colors.status.success;
  }
  if (t === 'REDEMPTION' || t === 'VOID') return brand.secondaryColor;
  if (t === 'ADJUSTMENT') return brand.accentColor.trim() || teboxDesignSystem.colors.accent.primaryDark;
  return teboxDesignSystem.colors.border.dark;
}

function giftCardBrandChrome(brand: GolferGiftCardBrand) {
  return bookingBrandInfoChromeStyles({
    primaryColor: brand.primaryColor,
    buttonColor: brand.accentColor,
    tintMixPercent: 6,
    borderMixPercent: 14,
  });
}

function BookingGolferGiftCardVisual({
  card,
  brand,
}: {
  card: GiftCardSummary;
  brand: GolferGiftCardBrand;
}) {
  const sb = teboxDesignSystem.spacing;
  const chrome = giftCardBrandChrome(brand);
  const accent = brand.primaryColor.trim() || brand.accentColor.trim() || 'var(--brand-primary, #021B1B)';

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 280,
        marginInline: 'auto',
        aspectRatio: '1.586 / 1',
        boxSizing: 'border-box',
        padding: sb.md,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: brand.cardBorderRadius,
        border: chrome.border,
        borderLeft: `3px solid ${accent}`,
        backgroundColor: chrome.backgroundColor,
        fontFamily: brand.fontFamily,
        boxShadow: teboxDesignSystem.shadows.sm,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: sb.sm, alignItems: 'flex-start' }}>
        <span
          style={{
            ...teboxDesignSystem.typography.bodySmall,
            fontWeight: 500,
            fontFamily: brand.fontFamily,
            color: brand.secondaryColor,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Gift card
        </span>
        <GiftCardStatusPill status={card.status} brand={brand} />
      </div>
      <div
        style={{
          ...teboxDesignSystem.typography.bodySmall,
          fontWeight: 500,
          fontFamily: brand.fontFamily,
          letterSpacing: '0.14em',
          fontVariantNumeric: 'tabular-nums',
          color: brand.textColor,
        }}
      >
        {formatPanGroups(card)}
      </div>
      <div style={{ textAlign: 'right' }}>
        <div
          style={{
            ...teboxDesignSystem.typography.caption,
            fontFamily: brand.fontFamily,
            color: brand.secondaryColor,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            marginBottom: 4,
          }}
        >
          Balance
        </div>
        <div
          style={{
            ...teboxDesignSystem.typography.h3,
            fontWeight: 500,
            fontFamily: brand.fontFamily,
            fontVariantNumeric: 'tabular-nums',
            color: brand.textColor,
            lineHeight: 1.05,
          }}
        >
          ${card.balance.toFixed(2)}
        </div>
        <div
          style={{
            ...teboxDesignSystem.typography.caption,
            fontFamily: brand.fontFamily,
            color: brand.secondaryColor,
            marginTop: 4,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          of ${card.initialAmount.toFixed(2)} initial
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: sb.sm }}>
          <span
            style={{
              ...teboxDesignSystem.typography.caption,
              fontFamily: brand.fontFamily,
              color: brand.secondaryColor,
            }}
          >
            Purchased {card.purchasedAt ? DATE_SHORT.format(new Date(card.purchasedAt)) : '—'}
          </span>
          {card.expiresAt ? (
            <span
              style={{
                ...teboxDesignSystem.typography.caption,
                fontFamily: brand.fontFamily,
                color: brand.secondaryColor,
              }}
            >
              Expires {DATE_SHORT.format(new Date(card.expiresAt))}
            </span>
          ) : null}
        </div>
        {card.message ? (
          <span
            style={{
              ...teboxDesignSystem.typography.caption,
              fontFamily: brand.fontFamily,
              color: brand.secondaryColor,
              fontStyle: 'italic',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={card.message}
          >
            &ldquo;{card.message}&rdquo;
          </span>
        ) : null}
      </div>
    </div>
  );
}

function GiftCardStatusPill({ status, brand }: { status: string; brand: GolferGiftCardBrand }) {
  const label = canonicalStatusDisplayLabel(status) ?? status;
  return (
    <span
      {...bookingBrandInfoPillDataAttrs}
      style={getBookingBrandInfoPillStyles(giftCardStatusAccentHex(status, brand))}
    >
      {label}
    </span>
  );
}

function BookingGolferGiftCardDetailModal({
  cardId,
  customerId,
  brand,
  onClose,
}: {
  cardId: string;
  customerId: string;
  brand: GolferGiftCardBrand;
  onClose: () => void;
}) {
  useBookingTebox();
  const [detail, setDetail] = useState<GiftCardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await giftCardsService.getMyGiftCardById(cardId);
      setDetail(data);
    } catch {
      setError('Failed to load gift card');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sb = teboxDesignSystem.spacing;

  return (
    <div
      role="dialog"
      aria-modal
      aria-labelledby="gift-card-detail-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: sb.lg,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          maxHeight: '90vh',
          overflowY: 'auto',
          backgroundColor: teboxDesignSystem.colors.background.primary,
          border: giftCardBrandChrome(brand).border,
          borderRadius: brand.cardBorderRadius,
          padding: sb.lg,
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: sb.md,
            marginBottom: sb.lg,
          }}
        >
          <h2
            id="gift-card-detail-title"
            style={{
              ...teboxDesignSystem.typography.h4,
              margin: 0,
              fontFamily: brand.fontFamily,
              color: brand.textColor,
            }}
          >
            Gift card
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              ...teboxDesignSystem.typography.body,
              fontFamily: brand.fontFamily,
              color: brand.secondaryColor,
              padding: sb.xs,
            }}
          >
            Close
          </button>
        </div>

        {loading ? (
          <p style={{ ...teboxDesignSystem.typography.body, fontFamily: brand.fontFamily, color: brand.secondaryColor, margin: 0 }}>
            Loading…
          </p>
        ) : error ? (
          <p style={{ ...teboxDesignSystem.typography.body, fontFamily: brand.fontFamily, color: teboxDesignSystem.colors.status.error, margin: 0 }}>
            {error}
          </p>
        ) : detail ? (
          <>
            <BookingGolferGiftCardVisual card={detail} brand={brand} />
            <p
              style={{
                ...teboxDesignSystem.typography.caption,
                fontFamily: brand.fontFamily,
                color: brand.secondaryColor,
                marginTop: sb.md,
                marginBottom: 0,
              }}
            >
              {giftCardRoleForCustomer(detail, customerId)}
              {' · '}
              Purchased {detail.purchasedAt ? DATE_SHORT.format(new Date(detail.purchasedAt)) : '—'}
              {detail.expiresAt ? ` · Expires ${DATE_SHORT.format(new Date(detail.expiresAt))}` : ''}
            </p>
            {detail.message ? (
              <p
                style={{
                  ...teboxDesignSystem.typography.bodySmall,
                  fontFamily: brand.fontFamily,
                  color: brand.secondaryColor,
                  fontStyle: 'italic',
                  marginTop: sb.sm,
                  marginBottom: 0,
                }}
              >
                &ldquo;{detail.message}&rdquo;
              </p>
            ) : null}

            {isUsableGiftCard(detail) ? (
              <div style={{ marginTop: sb.lg }}>
                {detail.mobilePassAccessToken ? (
                  <WalletAddButtons
                    title="Add to mobile wallet"
                    mobilePassResource={{
                      kind: 'gift_card',
                      id: detail.id,
                      accessToken: detail.mobilePassAccessToken,
                    }}
                  />
                ) : (
                  <p
                    style={{
                      ...teboxDesignSystem.typography.caption,
                      fontFamily: brand.fontFamily,
                      color: teboxDesignSystem.colors.status.error,
                      margin: 0,
                    }}
                  >
                    Apple Wallet is unavailable — refresh the page and try again.
                  </p>
                )}
              </div>
            ) : null}

            <h3
              style={{
                ...teboxDesignSystem.typography.body,
                fontWeight: 500,
                fontFamily: brand.fontFamily,
                marginTop: sb.lg,
                marginBottom: sb.md,
                color: brand.textColor,
              }}
            >
              Activity
            </h3>
            {!detail.events?.length ? (
              <p style={{ ...teboxDesignSystem.typography.bodySmall, fontFamily: brand.fontFamily, color: brand.secondaryColor, margin: 0 }}>
                No activity yet.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
                {detail.events.map((event, index) => {
                  const debit = giftCardEventAmountIsDebit(event.type);
                  const isLast = index === detail.events!.length - 1;
                  return (
                    <li
                      key={event.id}
                      style={{ display: 'flex', gap: sb.md, paddingBottom: isLast ? 0 : sb.md }}
                    >
                      <div
                        aria-hidden
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: 12,
                          paddingTop: 4,
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: eventDotColor(event.type, brand),
                            border: `2px solid ${teboxDesignSystem.colors.background.primary}`,
                            boxShadow: `0 0 0 1px ${teboxDesignSystem.colors.border.light}`,
                          }}
                        />
                        {!isLast ? (
                          <div
                            style={{
                              flex: 1,
                              width: 2,
                              minHeight: 24,
                              marginTop: 4,
                              backgroundColor: teboxDesignSystem.colors.border.light,
                            }}
                          />
                        ) : null}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: sb.md,
                            flexWrap: 'wrap',
                          }}
                        >
                          <div>
                            <div
                              style={{
                                ...teboxDesignSystem.typography.bodySmall,
                                fontWeight: 500,
                                fontFamily: brand.fontFamily,
                                color: brand.textColor,
                              }}
                            >
                              {giftCardEventLabel(event.type)}
                            </div>
                            <div
                              style={{
                                ...teboxDesignSystem.typography.caption,
                                fontFamily: brand.fontFamily,
                                color: brand.secondaryColor,
                                marginTop: 2,
                              }}
                            >
                              {DATE_TIME.format(new Date(event.createdAt))}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div
                              style={{
                                ...teboxDesignSystem.typography.bodySmall,
                                fontWeight: 500,
                                fontVariantNumeric: 'tabular-nums',
                                fontFamily: brand.fontFamily,
                                color: debit ? brand.textColor : brand.primaryColor,
                              }}
                            >
                              {formatGiftCardEventAmount(event.type, event.amount)}
                            </div>
                            <div
                              style={{
                                ...teboxDesignSystem.typography.caption,
                                fontFamily: brand.fontFamily,
                                color: brand.secondaryColor,
                                marginTop: 2,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              Balance {formatGiftCardBalanceAfter(event.balanceAfter)}
                            </div>
                          </div>
                        </div>
                        {event.memo ? (
                          <p
                            style={{
                              ...teboxDesignSystem.typography.caption,
                              fontFamily: brand.fontFamily,
                              color: brand.secondaryColor,
                              marginTop: sb.sm,
                              marginBottom: 0,
                              lineHeight: 1.45,
                            }}
                          >
                            {event.memo}
                          </p>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

export interface BookingGolferGiftCardsPanelProps {
  cards: GiftCardSummary[];
  loading: boolean;
  customerId: string;
  brand: GolferGiftCardBrand;
}

const BookingGolferGiftCardsPanel: React.FC<BookingGolferGiftCardsPanelProps> = ({
  cards,
  loading,
  customerId,
  brand,
}) => {
  useBookingTebox();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const sb = teboxDesignSystem.spacing;

  const kpis = useMemo(() => {
    const active = cards.filter(isUsableGiftCard);
    return {
      activeBalance: active.reduce((sum, c) => sum + c.balance, 0),
      activeCount: active.length,
    };
  }, [cards]);

  if (loading) {
    return (
      <p style={{ ...teboxDesignSystem.typography.body, fontFamily: brand.fontFamily, color: brand.secondaryColor, margin: 0 }}>
        Loading gift cards…
      </p>
    );
  }

  if (!cards.length) {
    return (
      <div style={{ textAlign: 'center', padding: `${sb.xl} ${sb.md}` }}>
        <p style={{ ...teboxDesignSystem.typography.body, fontFamily: brand.fontFamily, color: brand.textColor, margin: 0, maxWidth: 420, marginInline: 'auto' }}>
          You don&apos;t have any gift cards yet. Cards purchased for you or assigned to your account will appear
          here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: sb.lg,
          marginBottom: sb.lg,
        }}
      >
        <div>
          <div
            style={{
              ...teboxDesignSystem.typography.caption,
              fontFamily: brand.fontFamily,
              color: brand.secondaryColor,
              marginBottom: 4,
            }}
          >
            Active balance
          </div>
          <div
            style={{
              ...teboxDesignSystem.typography.h3,
              fontWeight: 500,
              fontFamily: brand.fontFamily,
              fontVariantNumeric: 'tabular-nums',
              color: brand.textColor,
            }}
          >
            ${kpis.activeBalance.toFixed(2)}
          </div>
        </div>
        <div>
          <div
            style={{
              ...teboxDesignSystem.typography.caption,
              fontFamily: brand.fontFamily,
              color: brand.secondaryColor,
              marginBottom: 4,
            }}
          >
            Active cards
          </div>
          <div
            style={{
              ...teboxDesignSystem.typography.h3,
              fontWeight: 500,
              fontFamily: brand.fontFamily,
              color: brand.textColor,
            }}
          >
            {kpis.activeCount}
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: sb.lg,
          width: '100%',
        }}
      >
        {cards.map((card) => {
          const inactive = !isUsableGiftCard(card);
          return (
            <div key={card.id} style={{ display: 'flex', flexDirection: 'column', gap: sb.sm, minWidth: 0 }}>
              <button
                type="button"
                onClick={() => setSelectedCardId(card.id)}
                aria-label={`Gift card ending ${card.codeLast4}, balance $${card.balance.toFixed(2)}`}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  opacity: inactive ? 0.88 : 1,
                  fontFamily: teboxDesignSystem.typography.fontFamily,
                }}
              >
                <BookingGolferGiftCardVisual card={card} brand={brand} />
              </button>
              <p
                style={{
                  ...teboxDesignSystem.typography.caption,
                  fontFamily: brand.fontFamily,
                  color: brand.secondaryColor,
                  margin: 0,
                  textAlign: 'center',
                }}
              >
                {giftCardRoleForCustomer(card, customerId)}
              </p>
            </div>
          );
        })}
      </div>

      {selectedCardId ? (
        <BookingGolferGiftCardDetailModal
          cardId={selectedCardId}
          customerId={customerId}
          brand={brand}
          onClose={() => setSelectedCardId(null)}
        />
      ) : null}
    </>
  );
};

export default BookingGolferGiftCardsPanel;
